import _ from 'lodash'
import {useAppConfig} from './AppConfigContext'

/**
 * Returns the full labels config for the given org_type (only active fields included).
 * Shape: { member: { label, description, default, options, active }, ... }
 */
export function useOrgTypeLabels(org_type) {
    const {org_type_configs = {}} = useAppConfig('org_type_configs')
    const key = String(org_type ?? '')
    return _.get(org_type_configs, key) || org_type_configs.default || {}
}

/**
 * Returns the active-only fields for the given org_type as an array.
 * Useful for rendering form rows.
 */
export function useActiveOrgTypeFields(org_type) {
    const fields = useOrgTypeLabels(org_type)
    return Object.entries(fields)
        .filter(([, field]) => field.active === true)
        .map(([key, field]) => ({key, ...field}))
}

/**
 * Returns the resolved display term for a single field key.
 * Priority: label_privacy saved on org → config default → config label → null
 *
 * Trả về null (không phải fieldKey) khi không xác định được — mọi call site đều
 * tự fallback qua `|| t('...')`, tránh lộ fieldKey nội bộ (vd "group") ra UI khi
 * org_type_configs chưa load xong hoặc label_privacy đang lưu dưới key cũ.
 *
 * @param {number|string} org_type  - e.g. 300 for parish
 * @param {string}        fieldKey  - e.g. 'leader', 'member', 'group'
 * @param {object|null}   label_privacy - org.label_privacy from the transformer
 */
export function getOrgTypeLabel(org_type, fieldKey, label_privacy, fieldConfig) {
    if (label_privacy && label_privacy[fieldKey]) return label_privacy[fieldKey]
    if (!fieldConfig) return null
    return fieldConfig.default || fieldConfig.label || ""
}

/**
 * Hook version of getOrgTypeLabel — reads org_type_configs from context.
 * Does NOT read label_privacy; pass it explicitly if available.
 */
export function useOrgTypeLabel(org_type, fieldKey, label_privacy) {
    const config = useOrgTypeLabels(org_type)
    const field = _.get(config, fieldKey, {})
    return getOrgTypeLabel(org_type, fieldKey, label_privacy, field)
}
